import type { TokenRewardPrize } from "@/domain/gems-game/domain/lib/Earning";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";
import { useWeeklyRewards } from "../components/WeeklyRewards/WeeklyRewardsProvider";

export function useRewardsShare() {
  const { latestClaimedTicket, sweepstakeTickets } = useWeeklyRewards();

  const { socialShare } = useFlags();

  const waitToClaimTicket = useMemo(
    () =>
      sweepstakeTickets?.find(
        (ticket) => ticket.status === "claimable" || ticket.status === "locked",
      ),
    [sweepstakeTickets],
  );

  const imxAmount = useMemo(() => {
    if (!latestClaimedTicket?.metadata) {
      return 0;
    }

    return latestClaimedTicket.metadata
      .filter((item) => item.type === "token")
      .reduce((sum, item) => {
        const tokenPrize = item as TokenRewardPrize;
        return sum + Number(tokenPrize.metadata.amount);
      }, 0);
  }, [latestClaimedTicket]);

  const formattedImxAmount = useMemo(() => {
    try {
      const imxFormatter = new Intl.NumberFormat("en-US", {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      });
      return imxFormatter.format(imxAmount);
    } catch (e) {
      return imxAmount.toString();
    }
  }, [imxAmount]);

  const shareUrl = useMemo(() => {
    try {
      const rarity = waitToClaimTicket?.rarity || latestClaimedTicket?.rarity;

      if (rarity) {
        const rarityBase64 = btoa(rarity);
        return `${window.location.origin}/rewards?share=${encodeURIComponent(rarityBase64)}`;
      }

      return `${window.location.origin}/rewards`;
    } catch (e) {
      return `${window.location.origin}/rewards`;
    }
  }, [waitToClaimTicket, latestClaimedTicket]);

  const shareText = useMemo(() => {
    if (!waitToClaimTicket && !latestClaimedTicket) {
      return "";
    }

    // For waiting to claim ticket (key only, no reward yet)
    if (waitToClaimTicket) {
      const rarity = waitToClaimTicket.rarity || "Weekly";
      return `Never stop gaming on Immutable Play.

@Immutable has built a fun product designed to reward real players and last forever.

Just unlocked my ${rarity} Key in the Weekly Rewards Draw.

Unlock yours now 👇
${shareUrl}`;
    }

    // For claimed tickets with rewards
    const rarity = latestClaimedTicket?.rarity || "Weekly";

    // If IMX was won
    if (imxAmount > 0) {
      return `Never stop gaming on Immutable Play.

@Immutable has built a fun product designed to reward real players and last forever.

Just won ${formattedImxAmount} IMX tokens from my ${rarity} Key in the Weekly Rewards Draw.

Claim yours now 👇
${shareUrl}`;
    }

    // For claimed tickets without IMX rewards
    return `Never stop gaming on Immutable Play.

@Immutable has built a fun product designed to reward real players and last forever.

Just claimed my ${rarity} Key from the Weekly Rewards Draw.

Claim yours now 👇
${shareUrl}`;
  }, [
    waitToClaimTicket,
    latestClaimedTicket,
    formattedImxAmount,
    imxAmount,
    shareUrl,
  ]);

  const trackingExtras = useMemo(
    () => ({
      rarity: waitToClaimTicket?.rarity || latestClaimedTicket?.rarity,
      imxAmount: formattedImxAmount,
    }),
    [waitToClaimTicket, latestClaimedTicket, formattedImxAmount],
  );

  const shouldShowShareButtons = useMemo(
    () => socialShare && (waitToClaimTicket || latestClaimedTicket),
    [socialShare, waitToClaimTicket, latestClaimedTicket],
  );

  return {
    shareText,
    shareUrl,
    trackingExtras,
    shouldShowShareButtons,
  };
}
